// frontend/src/pages/Products/Solutions.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import autoLabeler1 from '../Products/img/auto-labeler-1.jpg';
import autoLabeler2 from '../Products/img/auto-labeler-2.jpg';

const ensureSolutionsI18n = (i18n) => {
  const ns = 'solutions';
  if (!i18n.hasResourceBundle('zh', ns)) {
    i18n.addResourceBundle('zh', ns, {
      pageTitle: '標籤機 / 噴墨打標機',
      sectionTitleLabeler: '標籤機（貼標機）',
      sectionTitleInkjet: '噴墨打標機',
      labelerSpecsTitle: '規格',
      inkjetSpecsTitle: '規格 / 功能',
      labelerSpecs: [
        '尺寸：270 × 270 × 260 mm（高 × 寬 × 深）',
        '最高速度：300 M / min',
        '最快處理速度：10 Piece / sec Max',
        '標準標籤尺寸：20 × 30 mm（長 × 寬）',
        '標籤：用戶可客製',
        '電源：220V AC',
        '安裝位置：安裝在生產線兩側（如不用可移開）'
      ],
      labelerFeaturesTitle: '特點',
      labelerFeatures: [
        '光纖傳輸給定位器，自動補償不準確資訊',
        '配備壓力輥以確保良好貼合效果',
        '提供無紙報警連接（最大 DC 24V, 1A）',
        '採用摩擦方式貼合在產品上，使工作速度更加穩定'
      ],
      inkjetFunctions: [
        '即時打標：利用氣壓動作快速標記附近缺陷（自動尋邊）',
        '最高速度：3000 M / min',
        '最大打標寬度：25 mm',
        '標準移動距離：800 mm（最大 9 米）',
        '反應時間：100 毫秒'
      ],
      inkjetHeadTitle: '噴頭 / 耗材',
      inkjetHead: [
        '硬毛毯：75 × 25 × 130 mm（耗材）',
        '墨瓶：塑膠瓶 200 c.c.',
        '空氣壓力氣缸：運行行程 15 mm（需要 5 kg/mm² 氣壓）',
        '光纖傳感器：Omron E3X-NAII'
      ],
      inkjetTransmissionTitle: '傳輸部分',
      inkjetTransmission: [
        '發動機：智慧發動機（內建 PLC）',
        '齒輪組：高硬度齒輪',
        '軌道：低重量鋁製',
        '定位：光學標記閱讀器 EE-SX672'
      ],
      inkjetElectricalTitle: '電氣控制',
      inkjetElectrical: [
        '系統電壓：最大 220V, 1Φ, 3A',
        '最大功率：250W',
        '介面：RS-232',
        '輸入與輸出控制箱尺寸：350 × 450 × 200 mm'
      ],
      inkjetSoftwareTitle: '軟體',
      inkjetSoftware: [
        '軟體升級：智慧電機介面',
        '監管：人機介面（HMI）'
      ]
    });
  }
  if (!i18n.hasResourceBundle('en', ns)) {
    i18n.addResourceBundle('en', ns, {
      pageTitle: 'Labeler / Inkjet Marker',
      sectionTitleLabeler: 'Labeler (Label Applying Machine)',
      sectionTitleInkjet: 'Inkjet Marking Machine',
      labelerSpecsTitle: 'Specifications',
      inkjetSpecsTitle: 'Specs / Functions',
      labelerSpecs: [
        'Dimensions: 270 × 270 × 260 mm (H × W × D)',
        'Max line speed: 300 M / min',
        'Max handling rate: 10 pieces / sec (Max)',
        'Standard label size: 20 × 30 mm (L × W)',
        'Labels: User customized',
        'Power: 220V AC',
        'Mounting: Can be installed on both sides of the production line (removable when not needed)'
      ],
      labelerFeaturesTitle: 'Features',
      labelerFeatures: [
        'Optical fiber transmission to positioner with automatic compensation',
        'Equipped with pressure roller for optimal adhesion',
        'Paper-out alarm connection (Max DC 24V, 1A)',
        'Friction-based application for stable high-speed operation'
      ],
      inkjetFunctions: [
        'Real-time marking: Uses pneumatic actuation to quickly mark nearby defects (auto edge finding)',
        'Max speed: 3000 M / min',
        'Max marking width: 25 mm',
        'Standard travel: 800 mm (up to 9 m)',
        'Response time: 100 ms'
      ],
      inkjetHeadTitle: 'Print Head / Consumables',
      inkjetHead: [
        'Hard felt: 75 × 25 × 130 mm (consumable)',
        'Ink bottle: Plastic bottle 200 c.c.',
        'Air pressure cylinder: 15 mm stroke (requires 5 kg/mm² air pressure)',
        'Fiber sensor: Omron E3X-NAII'
      ],
      inkjetTransmissionTitle: 'Drive / Transmission',
      inkjetTransmission: [
        'Motor: Intelligent motor (integrated PLC)',
        'Gear set: High hardness gears',
        'Rail: Lightweight aluminum',
        'Positioning: Optical mark reader EE-SX672'
      ],
      inkjetElectricalTitle: 'Electrical Control',
      inkjetElectrical: [
        'System voltage: Max 220V, 1Φ, 3A',
        'Max power: 250W',
        'Interface: RS-232',
        'I/O control box size: 350 × 450 × 200 mm'
      ],
      inkjetSoftwareTitle: 'Software',
      inkjetSoftware: [
        'Software upgrade: Intelligent motor interface',
        'Monitoring: HMI (Human-Machine Interface)'
      ]
    });
  }
};

const Solutions = () => {
  const { t, i18n } = useTranslation();
  ensureSolutionsI18n(i18n);

  const safeList = (key) => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };

  const labelerImages = useMemo(() => ([
    { src: autoLabeler1, alt: 'Labeler 1' },
    { src: autoLabeler2, alt: 'Labeler 2' },
  ]), []);

  const imageBoxStyle = {
    width: '220px',
    maxWidth: '100%',
    margin: '0 auto',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,.15)',
    background: '#111'
  };

  const imgStyle = {
    width: '100%',
    height: 'auto',
    display: 'block',
    objectFit: 'contain',
    filter: 'contrast(1.05)'
  };

  const figCaptionStyle = {
    fontSize: '.6rem',
    color: '#666',
    textAlign: 'center',
    marginTop: '.4rem'
  };

  return (
    <div>
      <section className="solutions-section relative text-white py-20">
        <div className="absolute inset-0 bg-black bg-opacity-70" />
        <div className="container mx-auto px-4 relative z-10">
          <h1 className="text-5xl font-bold mb-4">{t('solutions:pageTitle')}</h1>
          <p className="text-xl text-white">
            {t('solutions:sectionTitleLabeler')} &middot; {t('solutions:sectionTitleInkjet')}
          </p>
        </div>
      </section>

      <main style={{ padding: 'clamp(1rem,2vw,2.5rem)', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif', lineHeight: 1.6 }}>
        <section style={{ marginBottom: '3rem' }}>
          <header style={{ marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: 'clamp(1.4rem,3vw,2.1rem)', margin: 0 }}>{t('solutions:sectionTitleLabeler')}</h2>
          </header>
          <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, 300px),1fr))' }}>
            <article>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 .5rem' }}>{t('solutions:labelerSpecsTitle')}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.85rem' }}>
                {safeList('solutions:labelerSpecs').map((line, i) => <li key={i} style={{ marginBottom: '.35rem' }}>{line}</li>)}
              </ul>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '1rem 0 .5rem' }}>{t('solutions:labelerFeaturesTitle')}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.85rem' }}>
                {safeList('solutions:labelerFeatures').map((line, i) => <li key={i} style={{ marginBottom: '.35rem' }}>{line}</li>)}
              </ul>
            </article>
            <figure style={{ margin: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={imageBoxStyle}>
                <img src={labelerImages[0].src} alt={labelerImages[0].alt} style={imgStyle} loading="lazy" />
              </div>
              <figcaption style={figCaptionStyle}>{labelerImages[0].alt}</figcaption>
            </figure>
          </div>
        </section>

        <section style={{ marginBottom: '3rem' }}>
          <header style={{ marginBottom: '1.25rem' }}>
            <h2 style={{ fontSize: 'clamp(1.4rem,3vw,2.1rem)', margin: 0 }}>{t('solutions:sectionTitleInkjet')}</h2>
          </header>
          <div style={{ display: 'grid', gap: '2rem', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, 300px),1fr))' }}>
            <article>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 .5rem' }}>{t('solutions:inkjetSpecsTitle')}</h3>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.85rem' }}>
                {safeList('solutions:inkjetFunctions').map((line, i) => <li key={i} style={{ marginBottom: '.35rem' }}>{line}</li>)}
              </ul>

              <h4 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1rem 0 .4rem' }}>{t('solutions:inkjetHeadTitle')}</h4>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.8rem' }}>
                {safeList('solutions:inkjetHead').map((line, i) => <li key={i} style={{ marginBottom: '.3rem' }}>{line}</li>)}
              </ul>

              <h4 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1rem 0 .4rem' }}>{t('solutions:inkjetTransmissionTitle')}</h4>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.8rem' }}>
                {safeList('solutions:inkjetTransmission').map((line, i) => <li key={i} style={{ marginBottom: '.3rem' }}>{line}</li>)}
              </ul>

              <h4 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1rem 0 .4rem' }}>{t('solutions:inkjetElectricalTitle')}</h4>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.8rem' }}>
                {safeList('solutions:inkjetElectrical').map((line, i) => <li key={i} style={{ marginBottom: '.3rem' }}>{line}</li>)}
              </ul>

              <h4 style={{ fontSize: '.9rem', fontWeight: 600, margin: '1rem 0 .4rem' }}>{t('solutions:inkjetSoftwareTitle')}</h4>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '.8rem' }}>
                {safeList('solutions:inkjetSoftware').map((line, i) => <li key={i} style={{ marginBottom: '.3rem' }}>{line}</li>)}
              </ul>
            </article>
            <figure style={{ margin: 0, display: 'flex', flexDirection: 'column' }}>
              <div style={imageBoxStyle}>
                <img src={labelerImages[1].src} alt={labelerImages[1].alt} style={imgStyle} loading="lazy" />
              </div>
              <figcaption style={figCaptionStyle}>{labelerImages[1].alt}</figcaption>
            </figure>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Solutions;